import { RyeIcon } from "../rye/RyeIcon";
import { buildClasses } from "../utils/buildClasses";
import { MockAppFrame, MockPanel } from "./MockAppFrame";

// A hand-built facsimile of the Budgets screen tracking a budget, for the
// marketing page.
//
// Deliberately NOT the real components: BudgetsScreen reads a dozen contexts
// and recomputes everything from live transactions, and its chart is drawn by
// Nivo with springs. This draws the same picture from fixed figures instead.
//
// Copied from the real app. If you change it, change these to match:
//   top bar      "Budgets", the Track against budget | View budget only switch
//                centred, New budget at the right (BudgetsScreen)
//   header       the name as an outlined xl dropdown, the months and progress
//                beside it, Edit, Duplicate, and Delete at the right; mb-6
//                (BudgetHeader)
//   totals       three columns, gap-8, max-w-3xl: label, amount "of" budget,
//                and a thin h-1.5 bar, band -200 and fill -500
//                (CategoryTypeTotals)
//   controls     Chart | Table at the left; type filter and Raw | Normalized at
//                the right; pt-6 above, gap-4 below (BudgetTrackingReport)
//   chart        rows 34px apart, bands 70% of that and actual bars half the
//                band; labels in a 220px column; Actual, Budget, and Remaining
//                in 92px columns 16px past the bars (GroupedBarChart,
//                BudgetBars). Bands are the type's 200 stop lightened, and a
//                shade deeper up to what's budgeted by now.
//
// The figures are the same invented household as MoneyFlowMock: the actuals are
// its eight months, measured against a full-year budget. All data is
// fabricated. Never paste real financial data into this file.

// Eight months into a twelve-month budget.
const FRACTION_OF_BUDGET_ELAPSED = 8 / 12;

type Kind = "income" | "expense" | "savings";

type Line = {
  emoji: string;
  name: string;
  // Signed as the app signs them: spending is negative.
  actual: number;
  budgeted: number;
};

type Group = {
  name: string;
  kind: Kind;
  expanded: boolean;
  lines: Line[];
};

const GROUPS: Group[] = [
  {
    name: "Income",
    kind: "income",
    expanded: false,
    lines: [
      { emoji: "💰", name: "Paycheck", actual: 48000, budgeted: 71100 },
      { emoji: "🤑", name: "Bonus", actual: 6000, budgeted: 12000 },
      { emoji: "🏦", name: "401k", actual: 7800, budgeted: 11700 },
      { emoji: "💵", name: "Misc. Income", actual: 1400, budgeted: 0 },
    ],
  },
  {
    name: "Fixed Expenses",
    kind: "expense",
    expanded: false,
    lines: [
      { emoji: "🏠", name: "Rent", actual: -19200, budgeted: -28800 },
      { emoji: "🥑", name: "Groceries", actual: -7200, budgeted: -10800 },
      { emoji: "🚗", name: "Transport", actual: -2200, budgeted: -3600 },
      { emoji: "⛽", name: "Gas", actual: -1600, budgeted: -2400 },
    ],
  },
  {
    // Expanded, because it's the group with a story: trips have already run
    // past their whole year's budget, which is exactly what this screen is for
    // noticing.
    name: "Discretionary Expenses",
    kind: "expense",
    expanded: true,
    lines: [
      { emoji: "🌴", name: "Trips", actual: -5300, budgeted: -4800 },
      { emoji: "🌮", name: "Restaurants", actual: -4500, budgeted: -6000 },
      { emoji: "🧺", name: "Other Expenses", actual: -4100, budgeted: -9600 },
      { emoji: "🎉", name: "Fun", actual: -2400, budgeted: -3600 },
      { emoji: "🎁", name: "Gifts", actual: -1100, budgeted: -2400 },
    ],
  },
  {
    name: "Savings",
    kind: "savings",
    expanded: false,
    lines: [
      { emoji: "📈", name: "401k Savings", actual: 7800, budgeted: 11700 },
      { emoji: "🚙", name: "New Car", actual: 4200, budgeted: 6300 },
      { emoji: "📊", name: "Brokerage", actual: 3600, budgeted: 4800 },
    ],
  },
];

// The three rows still waiting for review in TransactionsMock, which is the
// screen a visitor has just seen two steps earlier.
const UNREVIEWED_TOTAL = -(13.99 + 88.41 + 12.0);

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function groupActual(group: Group): number {
  return sum(group.lines.map((line) => line.actual));
}

function groupBudgeted(group: Group): number {
  return sum(group.lines.map((line) => line.budgeted));
}

function totalFor(kind: Kind, pick: (group: Group) => number): number {
  return sum(GROUPS.filter((group) => group.kind === kind).map(pick));
}

// BudgetBars' colors: band, band up to date, category bar, group bar.
const BAR_COLORS: Record<
  Kind,
  { band: string; toDate: string; category: string; group: string }
> = {
  expense: {
    band: "#C4E9FD",
    toDate: "#9CDCFC",
    category: "#0ea5e9",
    group: "#0284c7",
  },
  income: {
    band: "#BBEFD5",
    toDate: "#9FE8C4",
    category: "#3FD088",
    group: "#2AAC6B",
  },
  savings: {
    band: "#DDD9FF",
    toDate: "#CAC3FF",
    category: "#7d57ff",
    group: "#6930f7",
  },
};
const UNREVIEWED_BAR = "#FECF21";

const ROW_HEIGHT_PX = 34;
const BAND_HEIGHT_PX = ROW_HEIGHT_PX * 0.7;
const ACTUAL_HEIGHT_PX = BAND_HEIGHT_PX * 0.5;
const LABEL_COLUMN_WIDTH_PX = 220;
const NUMBER_COLUMN_WIDTH_PX = 92;
const NUMBER_COLUMN_GAP_PX = 16;
const COLUMNS = ["Actual", "Budget", "Remaining"];

// The value axis: $0 to $100K, labelled every $20K.
const AXIS_MAX = 100000;
const AXIS_TICKS = [0, 20000, 40000, 60000, 80000, 100000];

function percentOfAxis(amount: number): string {
  return `${(Math.abs(amount) / AXIS_MAX) * 100}%`;
}

function formatSigned(amount: number): string {
  const digits = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.abs(amount));
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${sign}$${digits}`;
}

function formatPlain(amount: number): string {
  return `$${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.abs(amount))}`;
}

// A RyeButton at size sm: outlined, subdued.
function MockButton(props: { icon: string; text?: string }): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "h-7",
        "shrink-0",
        "items-center",
        "gap-1.5",
        "rounded-md",
        "border",
        "border-on-surface-200",
        "text-sm",
        "text-on-surface-500",
        {
          if: props.text !== undefined,
          then: "px-2.5",
          else: "w-7 justify-center",
        },
      )}
    >
      <RyeIcon name={props.icon} size="sm" />
      {props.text && (
        <span className={buildClasses("text-sm")}>{props.text}</span>
      )}
    </div>
  );
}

// A RyeSegmentedButton, the first entry selected.
function MockSegmented(props: {
  entries: { text: string; icon?: string }[];
}): JSX.Element {
  return (
    <div
      className={buildClasses(
        "flex",
        "h-7",
        "shrink-0",
        "items-center",
        "rounded-lg",
        "bg-well",
        "p-0.5",
        "text-sm",
      )}
    >
      {props.entries.map((entry, index) => (
        <div
          key={entry.text}
          className={buildClasses(
            "flex",
            "h-full",
            "items-center",
            "gap-1.5",
            "whitespace-nowrap",
            "rounded-md",
            "px-3",
            "text-sm",
            {
              if: index === 0,
              then: buildClasses(
                "bg-surface-50",
                "text-on-surface-900",
                "shadow-raised",
              ),
              else: "text-on-surface-500",
            },
          )}
        >
          {entry.icon && <RyeIcon name={entry.icon} size="sm" />}
          {entry.text}
        </div>
      ))}
    </div>
  );
}

function TopBar(): JSX.Element {
  return (
    <div className={buildClasses("shrink-0", "px-[18px]")}>
      <div
        className={buildClasses(
          "relative",
          "flex",
          "h-11",
          "items-center",
          "justify-between",
        )}
      >
        <div className={buildClasses("text-base", "text-on-surface-700")}>
          Budgets
        </div>
        <div
          className={buildClasses("absolute", "left-1/2", "-translate-x-1/2")}
        >
          <MockSegmented
            entries={[
              { text: "Track against budget", icon: "monitoring" },
              { text: "View budget only", icon: "view_list" },
            ]}
          />
        </div>
        <MockButton icon="add" text="New budget" />
      </div>
    </div>
  );
}

function BudgetHeader(): JSX.Element {
  return (
    <div
      className={buildClasses(
        "mb-6",
        "flex",
        "min-h-11",
        "items-center",
        "gap-3",
      )}
    >
      {/* The name: an outlined RyeDropDown at size xl. */}
      <div
        className={buildClasses(
          "flex",
          "h-11",
          "items-center",
          "gap-3",
          "rounded-md",
          "border",
          "border-on-surface-300",
          "pl-3",
          "pr-2",
          "text-xl",
          "font-medium",
          "text-on-surface-800",
        )}
      >
        2026 Budget
        <RyeIcon
          name="keyboard_arrow_down"
          className={buildClasses("text-on-surface-500")}
        />
      </div>
      <div
        className={buildClasses(
          "flex",
          "shrink-0",
          "items-center",
          "gap-1.5",
          "text-sm",
          "text-on-surface-600",
        )}
      >
        <RyeIcon name="calendar_month" size="sm" />
        <span className={buildClasses("text-sm")}>Jan – Dec 2026</span>
        <span className={buildClasses("text-sm", "text-on-surface-500")}>
          · {Math.round(FRACTION_OF_BUDGET_ELAPSED * 100)}% complete
        </span>
      </div>
      <div className={buildClasses("ml-auto", "flex", "items-center", "gap-2")}>
        <MockButton icon="edit" text="Edit" />
        <MockButton icon="content_copy" />
        <MockButton icon="delete" />
      </div>
    </div>
  );
}

const TOTAL_COLORS: Record<Kind, { band: string; fill: string }> = {
  income: { band: "bg-green-200", fill: "bg-green-500" },
  expense: { band: "bg-blue-200", fill: "bg-blue-500" },
  savings: { band: "bg-purple-200", fill: "bg-purple-500" },
};

function Totals(): JSX.Element {
  const totals: { label: string; kind: Kind }[] = [
    { label: "Income", kind: "income" },
    { label: "Expenses", kind: "expense" },
    { label: "Savings", kind: "savings" },
  ];
  return (
    <div
      className={buildClasses(
        "mb-2",
        "grid",
        "max-w-3xl",
        "grid-cols-3",
        "gap-8",
      )}
    >
      {totals.map(({ label, kind }) => {
        // In their natural direction, as the app shows them.
        const amount = Math.abs(totalFor(kind, groupActual));
        const budgeted = Math.abs(totalFor(kind, groupBudgeted));
        const reached = Math.min(amount / budgeted, 1);
        return (
          <div
            key={label}
            className={buildClasses("flex", "min-w-0", "flex-col", "gap-1.5")}
          >
            <span className={buildClasses("text-sm", "text-on-surface-500")}>
              {label}
            </span>
            <div
              className={buildClasses(
                "flex",
                "items-baseline",
                "gap-1.5",
                "whitespace-nowrap",
              )}
            >
              <span
                className={buildClasses(
                  "text-lg",
                  "font-medium",
                  "text-on-surface-800",
                )}
              >
                {formatPlain(amount)}
              </span>
              <span className={buildClasses("text-sm", "text-on-surface-500")}>
                of {formatPlain(budgeted)}
              </span>
            </div>
            <div
              className={buildClasses(
                "h-1.5",
                "overflow-hidden",
                "rounded-full",
                TOTAL_COLORS[kind].band,
              )}
            >
              <div
                className={buildClasses(
                  "h-full",
                  "rounded-full",
                  TOTAL_COLORS[kind].fill,
                )}
                style={{ width: `${reached * 100}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Controls(): JSX.Element {
  return (
    <div className={buildClasses("flex", "items-center", "gap-3", "pt-6")}>
      <MockSegmented
        entries={[
          { text: "Chart", icon: "bar_chart" },
          { text: "Table", icon: "table" },
        ]}
      />
      <div className={buildClasses("ml-auto", "flex", "items-center", "gap-3")}>
        <MockSegmented
          entries={[
            { text: "All" },
            { text: "Expenses" },
            { text: "Income" },
            { text: "Savings" },
          ]}
        />
        <MockSegmented entries={[{ text: "Raw" }, { text: "Normalized" }]} />
      </div>
    </div>
  );
}

type ChartRow = {
  key: string;
  label: string;
  emoji?: string;
  // Group rows have a chevron; null for the unreviewed row, which has none.
  expanded?: boolean | null;
  kind: Kind | "unreviewed";
  isGroup: boolean;
  actual: number;
  // Zero when nothing is budgeted.
  budgeted: number;
};

const CHART_ROWS: ChartRow[] = [
  {
    key: "unreviewed",
    label: "Unreviewed",
    expanded: null,
    kind: "unreviewed",
    isGroup: true,
    actual: UNREVIEWED_TOTAL,
    budgeted: 0,
  },
  ...GROUPS.flatMap((group): ChartRow[] => [
    {
      key: group.name,
      label: group.name,
      expanded: group.expanded,
      kind: group.kind,
      isGroup: true,
      actual: groupActual(group),
      budgeted: groupBudgeted(group),
    },
    ...(group.expanded
      ? group.lines.map((line) => ({
          key: `${group.name}/${line.name}`,
          label: line.name,
          emoji: line.emoji,
          kind: group.kind,
          isGroup: false,
          actual: line.actual,
          budgeted: line.budgeted,
        }))
      : []),
  ]),
];

function RowLabel(props: { row: ChartRow }): JSX.Element {
  const { row } = props;
  return (
    <div
      className={buildClasses(
        "flex",
        "min-w-0",
        "items-center",
        "gap-1",
        "text-sm",
        {
          if: row.isGroup,
          then: "font-medium text-on-surface-800",
          else: "pl-7 text-on-surface-600",
        },
      )}
      style={{ width: LABEL_COLUMN_WIDTH_PX }}
    >
      {row.expanded !== undefined && row.expanded !== null && (
        <RyeIcon
          name="keyboard_arrow_right"
          size="sm"
          className={buildClasses("shrink-0", {
            if: row.expanded,
            then: "rotate-90",
          })}
        />
      )}
      {row.emoji && (
        <span className={buildClasses("mr-1", "shrink-0", "text-sm")}>
          {row.emoji}
        </span>
      )}
      <span className={buildClasses("truncate", "text-sm")}>{row.label}</span>
    </div>
  );
}

function RowBar(props: { row: ChartRow }): JSX.Element | null {
  const { row } = props;
  // An expanded group's bar shrinks away; its categories show the money.
  if (row.isGroup && row.expanded === true) return null;
  const colors = row.kind === "unreviewed" ? null : BAR_COLORS[row.kind];
  const actualColor =
    colors === null
      ? UNREVIEWED_BAR
      : row.isGroup
        ? colors.group
        : colors.category;
  return (
    <div
      className={buildClasses(
        "absolute",
        "inset-x-0",
        "top-1/2",
        "-translate-y-1/2",
      )}
      style={{ height: BAND_HEIGHT_PX }}
    >
      {colors !== null && row.budgeted !== 0 && (
        <div
          className={buildClasses(
            "absolute",
            "inset-y-0",
            "left-0",
            "overflow-hidden",
            "rounded-[3px]",
          )}
          style={{
            width: percentOfAxis(row.budgeted),
            background: colors.band,
          }}
        >
          <div
            className={buildClasses("h-full")}
            style={{
              width: `${FRACTION_OF_BUDGET_ELAPSED * 100}%`,
              background: colors.toDate,
            }}
          />
        </div>
      )}
      <div
        className={buildClasses(
          "absolute",
          "left-0",
          "top-1/2",
          "-translate-y-1/2",
          "rounded-[2px]",
        )}
        style={{
          width: percentOfAxis(row.actual),
          height: ACTUAL_HEIGHT_PX,
          background: actualColor,
        }}
      />
    </div>
  );
}

function RowNumbers(props: { row: ChartRow }): JSX.Element {
  const { row } = props;
  const hidden = row.isGroup && row.expanded === true;
  const hasBudget = row.budgeted !== 0;
  // In the budget's own direction: positive while there's room left.
  const left = hasBudget
    ? (row.budgeted - row.actual) * Math.sign(row.budgeted)
    : null;
  const isLimit = row.budgeted < 0;
  const overspent = isLimit && left !== null && left < 0;
  const remainingWord =
    left !== null && left < 0
      ? isLimit
        ? "over"
        : "extra"
      : isLimit
        ? "left"
        : "to go";
  const cells = [
    row.actual === 0 ? "—" : formatSigned(row.actual),
    hasBudget ? formatSigned(row.budgeted) : "—",
    left === null ? "—" : `${formatPlain(left)} ${remainingWord}`,
  ];
  return (
    <div
      className={buildClasses("flex", "shrink-0", "text-xs", {
        if: row.isGroup,
        then: "font-medium",
      })}
      style={{
        paddingLeft: NUMBER_COLUMN_GAP_PX,
        // The real chart fades an expanded group's numbers out with its bar.
        visibility: hidden ? "hidden" : undefined,
      }}
    >
      {cells.map((text, index) => (
        <span
          key={COLUMNS[index]}
          className={buildClasses(
            "whitespace-nowrap",
            "text-right",
            "text-xs",
            {
              if: index === 2 && overspent,
              then: "text-red-600",
              else: "text-on-surface-600",
            },
          )}
          style={{ width: NUMBER_COLUMN_WIDTH_PX }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

function Chart(): JSX.Element {
  return (
    <div className={buildClasses("flex", "flex-col")}>
      {/* Column headings, with Expand all over the row labels. */}
      <div
        className={buildClasses(
          "flex",
          "items-end",
          "text-xs",
          "font-medium",
          "text-on-surface-500",
        )}
      >
        <div
          className={buildClasses(
            "mr-auto",
            "flex",
            "h-7",
            "items-center",
            "gap-1.5",
            "text-sm",
            "font-normal",
          )}
        >
          <RyeIcon name="unfold_more" size="sm" />
          Expand all
        </div>
        {COLUMNS.map((column) => (
          <span
            key={column}
            className={buildClasses("text-right", "text-xs")}
            style={{ width: NUMBER_COLUMN_WIDTH_PX }}
          >
            {column}
          </span>
        ))}
      </div>
      <div className={buildClasses("flex", "pt-2")}>
        <div className={buildClasses("flex", "flex-col")}>
          {CHART_ROWS.map((row) => (
            <div
              key={row.key}
              className={buildClasses("flex", "items-center")}
              style={{ height: ROW_HEIGHT_PX }}
            >
              <RowLabel row={row} />
            </div>
          ))}
        </div>
        {/* The bars, over the value axis's grid lines. */}
        <div className={buildClasses("relative", "min-w-0", "flex-1")}>
          {AXIS_TICKS.map((tick) => (
            <div
              key={tick}
              className={buildClasses(
                "absolute",
                "top-0",
                "w-px",
                "bg-on-surface-200",
              )}
              style={{
                left: percentOfAxis(tick),
                height: CHART_ROWS.length * ROW_HEIGHT_PX,
              }}
            >
              <span
                className={buildClasses(
                  "absolute",
                  "left-1/2",
                  "-translate-x-1/2",
                  "pt-2",
                  "text-[11px]",
                  "text-on-surface-500",
                )}
                style={{ top: "100%" }}
              >
                {tick === 0 ? "$0" : `$${tick / 1000}K`}
              </span>
            </div>
          ))}
          {CHART_ROWS.map((row) => (
            <div
              key={row.key}
              className={buildClasses("relative")}
              style={{ height: ROW_HEIGHT_PX }}
            >
              <RowBar row={row} />
            </div>
          ))}
        </div>
        <div className={buildClasses("flex", "flex-col")}>
          {CHART_ROWS.map((row) => (
            <div
              key={row.key}
              className={buildClasses("flex", "items-center")}
              style={{ height: ROW_HEIGHT_PX }}
            >
              <RowNumbers row={row} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BudgetTrackingMock(): JSX.Element {
  return (
    <MockAppFrame activeTab="budgets">
      <MockPanel className="flex-col">
        <TopBar />
        <div
          className={buildClasses(
            "min-h-0",
            "flex-1",
            "overflow-hidden",
            "px-[18px]",
            "pt-[18px]",
          )}
        >
          <BudgetHeader />
          <Totals />
          <div className={buildClasses("flex", "flex-col", "gap-4")}>
            <Controls />
            <Chart />
          </div>
        </div>
      </MockPanel>
    </MockAppFrame>
  );
}
